'use client';
import React, { useEffect, useState } from 'react';
import styled from '@benzinga/themetron';
import { keyframes } from 'styled-components';
import classNames from 'classnames';
import { useIsUserLoggedIn, useUserSubscriptions } from '@benzinga/user-context';

interface ButtonBzProProps {
  label: string;
  proView?: string;
  url: string;
  variant?: 'trial' | 'with-arrow' | 'light';
}

// @TODO: proView is a future feature that can allow us to pass in a custom view for pro
export const ButtonBzPro = ({ label, proView = '', url, variant }: ButtonBzProProps) => {
  const [buttonLabel, setButtonLabel] = useState(label);
  const [buttonUrl, setButtonUrl] = useState(url);
  const isLoggedIn = useIsUserLoggedIn();
  const subscriptions = useUserSubscriptions();

  useEffect(() => {
    if (isLoggedIn && subscriptions && subscriptions.length > 0) {
      const hasProAccess = subscriptions?.find(sub => sub.product === 'benzinga-pro');
      if (hasProAccess) {
        setButtonLabel('Launch Benzinga Pro');
        setButtonUrl(`https://pro.benzinga.com/dashboard/${proView ? proView : ''}`);
      }
    }
  }, [isLoggedIn, label, proView, subscriptions, url]);

  return (
    <StyledButtonBzPro
      aria-label={buttonLabel}
      className={classNames('shine-effect text-white', {
        'block py-2 rounded-sm bg-bzblue-600 text-center mx-2 md:mx-4 my-2': variant === 'trial',
        'bz-pro-btn': variant === 'with-arrow' || variant === 'light',
      })}
      href={buttonUrl}
    >
      {buttonLabel} {variant === 'with-arrow' && <span className="arrow">→</span>}
    </StyledButtonBzPro>
  );
};

const animDuration = '5s';
const animSpeed = '0.65s';

const shineDegree = '120deg';
const shineColor = 'rgba(255, 255, 255, 0.2)';
const shineEffect = `linear-gradient(${shineDegree}, transparent, ${shineColor}, transparent)`;
const animationPercentage = (parseFloat(animSpeed) / (parseFloat(animDuration) + parseFloat(animSpeed))) * 100 + '%';

const shineAnimation = keyframes`
  0% {
    left: -100%;
    transition-property: left;
  }
  ${animationPercentage},
  100% {
    left: 100%;
    transition-property: left;
  }
`;

const StyledButtonBzPro = styled.a`
  position: relative;
  overflow: hidden;
  white-space: nowrap;

  &::before {
    animation: ${shineAnimation} ${animDuration} ease-in-out infinite;
    content: '';
    position: absolute;
    top: 0;
    width: 100%;
    height: 100%;
    background: ${shineEffect};
  }
`;
